import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface HashtagHold {
  id: string;
  tag_id: string;
  tag_name: string;
  coco_spent: number;
  started_at: string;
  status: "pending" | "active";
}

/** Client Supabase non typé : la table tag_holds arrive avec la migration du brouillon. */
const db = supabase as unknown as {
  from: (table: string) => any;
  rpc: (fn: string, args?: Record<string, unknown>) => Promise<{ data: any; error: any }>;
};

/**
 * Rôles et réservations de hashtags d'activité du compte connecté.
 * Un administrateur n'achète pas : canBuy reste faux pour lui.
 */
export const useHashtagHolds = (userId?: string | null) => {
  const [holds, setHolds] = useState<HashtagHold[]>([]);
  const [pendingHolds, setPendingHolds] = useState<HashtagHold[]>([]);
  const [canBuy, setCanBuy] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchHolds = useCallback(async () => {
    if (!userId) {
      setHolds([]);
      setPendingHolds([]);
      setCanBuy(false);
      setIsAdmin(false);
      return;
    }

    setLoading(true);

    const [{ data: adminFlag }, { data: buyFlag }] = await Promise.all([
      db.rpc("has_role", { _user_id: userId, _role: "admin" }),
      db.rpc("can_buy_hashtag", { _user_id: userId }),
    ]);

    setIsAdmin(Boolean(adminFlag));
    setCanBuy(Boolean(buyFlag));

    const { data } = await db
      .from("tag_holds")
      .select("id, tag_id, coco_spent, started_at, status, tags:tag_id(name)")
      .eq("user_id", userId)
      .in("status", ["pending", "active"])
      .order("started_at", { ascending: false });

    const rows: HashtagHold[] = ((data ?? []) as any[]).map((row) => ({
      id: String(row.id),
      tag_id: String(row.tag_id),
      tag_name: String(row.tags?.name ?? ""),
      coco_spent: Number(row.coco_spent ?? 0),
      started_at: String(row.started_at ?? ""),
      status: row.status === "pending" ? "pending" : "active",
    }));

    // `holds` ne contient que les réservations validées : elles seules sont posables.
    setHolds(rows.filter((row) => row.status === "active"));
    setPendingHolds(rows.filter((row) => row.status === "pending"));

    setLoading(false);
  }, [userId]);

  useEffect(() => {
    void fetchHolds();
  }, [fetchHolds]);

  return { holds, pendingHolds, canBuy, isAdmin, loading, refetch: fetchHolds };
};
